import { Container, Grid, LinearProgress, Typography, useTheme } from "@mui/material";
import { RootState } from "@store/store";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CheckIcon from "@mui/icons-material/Check";
import DownloadWaitIcon from "@assets/download_wait_icon.svg";
import DownloadWarningIcon from "@assets/download_warning_icon.svg";
import DownloadWarningYellowIcon from "@assets/download_warning_yellow_icon.svg";
import { useLazyGetPldSearchResultSumaryQuery } from "@services/api/pld";
import { PldSearchResultSumary } from "@interfaces/pld";
import { setReloadSection, setStatusAlertsPldReady } from "@store/slices/componentsSlice";

enum StatusPLD {
    HIT_ABORTIVO = 1,
    HIT_REVISION = 2,
    HIT_SINREVISION = 3,
    SIN_COINCIDENCIA = 4,
    PROCESANDO = 5,
}

export const EstatusAlertasPrePld = () => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(false);
    const [reloading, setReloading] = useState<boolean>(false);
    const [listFiltered, setListFiltered] = useState<Array<PldSearchResultSumary>>([]);

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);
    const printingMode = useSelector((state: RootState) => state.components.printingProfileReport.printingMode);

    const [triggerGetPldSRS] = useLazyGetPldSearchResultSumaryQuery();

    const createSectionStatus = (title: string, status: StatusPLD) => {
        return (
            <Grid>
                <Container
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    {status === StatusPLD.HIT_REVISION && <img src={DownloadWarningYellowIcon} />}
                    {status === StatusPLD.HIT_ABORTIVO && <img src={DownloadWarningIcon} />}
                    {status === StatusPLD.HIT_SINREVISION && (
                        <CheckIcon sx={{ color: "#528CD6", width: 13, height: 11 }} />
                    )}
                    {status === StatusPLD.SIN_COINCIDENCIA && (
                        <CheckIcon sx={{ color: "#528CD6", width: 13, height: 11 }} />
                    )}
                    {status === StatusPLD.PROCESANDO && <img src={DownloadWaitIcon} />}
                    <Typography variant="body3" fontWeight={500} color="#002652" mt={0.2}>
                        {title}
                    </Typography>
                    {status === StatusPLD.HIT_REVISION && (
                        <Typography variant="body3" color="main4" fontWeight={500}>
                            Hit - Revisión
                        </Typography>
                    )}
                    {status === StatusPLD.HIT_ABORTIVO && (
                        <Typography variant="body3" color="main4" fontWeight={500}>
                            Hit - Abortivo
                        </Typography>
                    )}
                    {status === StatusPLD.HIT_SINREVISION && (
                        <Typography variant="body3" color="main2" fontWeight={500}>
                            Hit - Sin Revisión
                        </Typography>
                    )}
                    {status === StatusPLD.SIN_COINCIDENCIA && (
                        <Typography variant="body3" color="main2" fontWeight={500}>
                            Sin Coincidencia
                        </Typography>
                    )}
                    {status === StatusPLD.PROCESANDO && (
                        <Typography variant="body3" color="secondary4" fontWeight={500}>
                            Procesando
                        </Typography>
                    )}
                </Container>
            </Grid>
        );
    };

    const reloadInfo = async () => {
        setReloading(false);

        try {
            const lists = await triggerGetPldSRS(applicationSelected.id ?? "0-0-0-0-0").unwrap();
            const listFiltered = lists.filter((list) =>
                ["PEPINT", "SAT69B", "SAT69", "OFAC", "ONU"].some((t) => t === list.lista),
            );
            const others = lists.filter((list) =>
                ["PEPINT", "SAT69B", "SAT69", "OFAC", "ONU"].some((t) => t !== list.lista),
            );
            let othersStatus = others.every((t) => t.estatus === StatusPLD.PROCESANDO) ? 5 : 0;
            othersStatus = others.every((t) => t.estatus === StatusPLD.SIN_COINCIDENCIA) ? 4 : othersStatus;
            othersStatus = others.some((t) => t.estatus === StatusPLD.HIT_SINREVISION) ? 3 : othersStatus;
            othersStatus = others.some((t) => t.estatus === StatusPLD.HIT_REVISION) ? 2 : othersStatus;
            othersStatus = others.some((t) => t.estatus === StatusPLD.HIT_ABORTIVO) ? 1 : othersStatus;

            listFiltered.push({ lista: "Otros", estatus: othersStatus });
            setListFiltered(listFiltered);

            if (printingMode === false) {
                if (lists.some((list) => list.estatus === StatusPLD.PROCESANDO)) {
                    setReloading(true);
                }
            }
        } catch (error) {
            console.log(`Error reloadInfo: ${error}`);
        } finally {
            if (printingMode) {
                dispatch(setStatusAlertsPldReady(true));
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        if (reloadSection) {
            setLoading(true);
            reloadInfo().finally(() => dispatch(setReloadSection(false)));
        }
    }, [reloadSection]);

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0" || applicationSelected.id === undefined) return;

        setLoading(true);
        reloadInfo();
    }, [applicationSelected.id]);

    useEffect(() => {
        if (reloading === false) return;
        setTimeout(() => {
            setLoading(true);
            reloadInfo();
        }, 5000);
    }, [reloading]);

    return (
        <Grid
            pt={2}
            rowSpacing={2}
            container
            size={9}
            sx={{ backgroundColor: theme.palette.mainBase30.main, borderRadius: "8px" }}
            justifyContent={"space-between"}
        >
            {listFiltered.map((result) => createSectionStatus(result.lista, result.estatus))}
            <Grid size={12} mx={0}>
                <LinearProgress sx={{ borderRadius: 25, visibility: loading ? "visible" : "hidden" }} />
            </Grid>
        </Grid>
    );
};
